import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ClipboardCheck, Cog, FileCheck2, ChevronRight, CircleDot, Loader2 } from "lucide-react";
import Header from "../components/Header";
import { useApp } from "../context/AppContext";
import { listEvidences } from "@/api/evidences";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useApp();
  const navigate = useNavigate();

  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listEvidences({ engineerUserUid: "me", limit: 100 });
        if (!cancelled) setInspections(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("dashboard load", err);
        if (!cancelled) toast.error("No se pudo cargar el historial");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const stats = {
    total: inspections.length,
    today: inspections.filter(
      (i) => new Date(i.createdAt).toDateString() === new Date().toDateString()
    ).length,
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-950" data-testid="dashboard-page">
      <Header
        title={`OP. ${user?.email?.split("@")[0]?.toUpperCase() || "—"}`}
        subtitle={user?.profile === "Engineer" ? "Ingeniero" : user?.profile}
        back={false}
        onLogout
      />

      <div className="flex-1 overflow-y-auto no-scrollbar px-5 pt-5 pb-24">
        <div className="relative bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5 overflow-hidden mb-5 corner-brackets">
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-orange-500/10 blur-2xl pointer-events-none" />
          <div className="flex items-center gap-2 mb-3">
            <CircleDot className="w-3 h-3 text-green-500 animate-pulse" />
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-600 dark:text-zinc-400">
              Sistema operativo
            </span>
          </div>
          <h2 className="font-display font-bold text-2xl uppercase leading-tight text-zinc-900 dark:text-white">
            Panel<br />de control
          </h2>
          <p className="font-mono-tech text-xs text-zinc-500 mt-1">
            Bienvenido al terminal de inspección
          </p>

          <div className="grid grid-cols-2 gap-3 mt-5">
            <div className="bg-white/80 dark:bg-zinc-950/60 border border-zinc-200 dark:border-zinc-800 rounded-sm p-3">
              <p className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500">
                Inspecciones
              </p>
              <p className="font-display font-bold text-3xl text-zinc-900 dark:text-white leading-none mt-1">
                {String(stats.total).padStart(2, "0")}
              </p>
            </div>
            <div className="bg-white/80 dark:bg-zinc-950/60 border border-zinc-200 dark:border-zinc-800 rounded-sm p-3">
              <p className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500">
                Hoy
              </p>
              <p className="font-display font-bold text-3xl text-orange-500 leading-none mt-1">
                {String(stats.today).padStart(2, "0")}
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate("/new")}
          data-testid="dashboard-new-inspection-btn"
          className="relative w-full bg-orange-500 hover:bg-orange-600 transition-colors rounded-md p-5 flex items-center gap-4 mb-6 active:scale-[0.99] glow-orange group overflow-hidden"
        >
          <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
          <div className="w-12 h-12 bg-black/30 rounded-sm flex items-center justify-center shrink-0">
            <Plus className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
          <div className="text-left flex-1 relative">
            <p className="font-mono-tech text-[9px] uppercase tracking-widest text-orange-100">
              // Nueva tarea
            </p>
            <p className="font-display font-bold text-xl uppercase tracking-wider text-white leading-tight">
              Iniciar inspección
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-white relative shrink-0" />
        </button>

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-orange-500" />
            <h3 className="font-display font-bold uppercase tracking-widest text-sm text-zinc-900 dark:text-white">
              Historial
            </h3>
          </div>
          <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-400 dark:text-zinc-600">
            {inspections.length} reg.
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
          </div>
        ) : inspections.length === 0 ? (
          <div className="border border-dashed border-zinc-300 dark:border-zinc-800 rounded-md p-8 text-center">
            <Cog className="w-8 h-8 text-zinc-400 dark:text-zinc-700 mx-auto mb-2" />
            <p className="font-mono-tech text-xs uppercase tracking-widest text-zinc-500">
              Sin inspecciones registradas
            </p>
            <p className="font-mono-tech text-[10px] text-zinc-400 dark:text-zinc-600 mt-1">
              Inicia una nueva inspección arriba
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="inspections-list">
            {inspections.map((insp) => {
              const firstImage = (insp.media || []).find((m) => m.type === "image");
              return (
                <Link
                  key={insp.uid}
                  to={`/evidence/${insp.uid}`}
                  className="flex items-center gap-3 p-3 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 hover:border-orange-500/60 rounded-md transition-colors"
                  data-testid="inspection-row"
                >
                  <div className="w-12 h-12 rounded-sm bg-zinc-100 dark:bg-zinc-800 overflow-hidden shrink-0 border border-zinc-200 dark:border-zinc-700">
                    {firstImage ? (
                      <img src={firstImage.url} alt={insp.formTypeName} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Cog className="w-5 h-5 text-zinc-400 dark:text-zinc-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-sm uppercase tracking-wider text-zinc-900 dark:text-white truncate">
                      {insp.formTypeName}
                    </p>
                    <p className="font-mono-tech text-[10px] text-zinc-500 truncate">
                      SN: {insp.serialNumber} · {new Date(insp.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <FileCheck2 className="w-3.5 h-3.5 text-green-500" />
                    <span className="font-mono-tech text-[9px] uppercase tracking-widest text-green-500">
                      OK
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
